import React from 'react';
import ReactDOM from 'react-dom';
import { uniqueId } from 'underscore';
import modalRegistry from './modalRegistry';

const CONTAINER_ID = 'portal-element-root';
const HOST_CLASS = 'dialog-host';

/**
 *  Singleton class to manage opening and closing instances of `ConfirmDialog`
 *
 * Usage:
 * // When you're ready to open
 * this.unmountDialog = dialogManage.openDialog(MyDialogClass, props);
 *
 * // When you're ready to close
 * this.unmountDialog();
 */
class DialogContainer {
  /**
   * Gets the container used by this manager.
   * @returns {DOMNode} The container
   */
  // eslint-disable-next-line class-methods-use-this
  getContainer() {
    let dialogContainer = document.getElementById(CONTAINER_ID);
    if (!dialogContainer) {
      const dialogHostCandidates = document.getElementsByClassName(HOST_CLASS);

      dialogContainer = document.createElement('div');
      dialogContainer.id = CONTAINER_ID;

      if (dialogHostCandidates.length) {
        const [dialogHost] = dialogHostCandidates;

        dialogHost.append(dialogContainer);
      } else {
        dialogContainer = null;
      }
    }

    return dialogContainer;
  }

  openDialog(dialogClass, props, requirePropTypes = true) {
    const noopFn = () => {};
    const dialogContainer = this.getContainer();
    const uuid = uniqueId('dialogManager');

    if (requirePropTypes && !dialogClass.propTypes) {
      console.error('The dialog class must have a valid set of proptypes');
      return noopFn;
    }
    if (!dialogContainer) {
      console.error('No container present to put your dialog');
      return noopFn;
    }

    let renderContainer = document.createElement('div');
    dialogContainer.appendChild(renderContainer);
    modalRegistry.registerModal({
      id: uuid,
      inPortalElementRoot: true,
    });

    const localProps = {
      ...props,
      renderContainer,
      initialOpen: true,
    };
    const dialog = React.createElement(dialogClass, localProps);

    ReactDOM.render(dialog, renderContainer);
    const unmount = () => {
      this.unmountDialog(renderContainer);
      modalRegistry.unregisterModal(uuid);
      renderContainer = null; // free up memory if this function is held around
    };
    return unmount;
  }

  /**
   * Unmount a dialog from it's renderContainer and then remove the container.
   *
   * @param {Element} renderContainer - The DOM node the dialog was rendered into.
   */
  // eslint-disable-next-line class-methods-use-this
  unmountDialog(renderContainer) {
    if (!renderContainer) {
      return;
    }
    ReactDOM.unmountComponentAtNode(renderContainer);
    if (renderContainer.parentNode) {
      renderContainer.parentNode.removeChild(renderContainer);
    }
  }
}
export default new DialogContainer();
